import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { caseStudies } from "@/data/caseStudiesData";

interface LocalSEOCasesProps {
  language: 'es' | 'en';
}

export const LocalSEOCases = ({ language }: LocalSEOCasesProps) => {
  const content = {
    es: {
      title: "Casos de éxito locales",
      subtitle: "Resultados reales en empresas de nuestra área de servicio.",
      viewCase: "Ver caso completo",
      viewAll: "Ver todos los casos",
      casesLink: "/casos-de-exito"
    },
    en: {
      title: "Local success cases",
      subtitle: "Real results in companies from our service area.",
      viewCase: "View full case",
      viewAll: "View all cases",
      casesLink: "/en/case-studies"
    }
  };

  const t = content[language];
  
  // Filter featured cases (limit to 3)
  const localCases = caseStudies.filter(cs => cs.featured).slice(0, 3);

  return (
    <section className="section-padding bg-secondary/30">
      <div className="container-custom">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 rounded-full text-brand text-sm font-medium mb-4">
            <Trophy className="w-4 h-4" />
            {language === 'es' ? 'Resultados documentados' : 'Documented results'}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-10">
          {localCases.map((caseStudy) => {
            const industry = language === 'es' ? caseStudy.industry : caseStudy.industryEn;
            const mainResult = language === 'es' 
              ? caseStudy.results[0]?.description 
              : caseStudy.results[0]?.descriptionEn;
            const services = language === 'es' ? caseStudy.services : caseStudy.servicesEn;
            const caseUrl = language === 'es' 
              ? `/casos-de-exito/${caseStudy.slug}` 
              : `/case-studies/${caseStudy.slug}`;
            
            return (
              <div 
                key={caseStudy.id}
                className="bg-background rounded-2xl border border-border overflow-hidden hover:shadow-lg transition-shadow group"
              >
                <div className="bg-brand/5 p-6 border-b border-border">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <MapPin className="w-4 h-4" />
                    {caseStudy.location}
                  </div>
                  <h3 className="text-lg font-bold text-foreground">
                    {industry}
                  </h3>
                </div>
                
                <div className="p-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {services.slice(0, 2).map((service, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-brand/10 text-brand text-xs font-medium rounded-full"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                  
                  <p className="text-foreground text-sm mb-4">
                    {mainResult}
                  </p>
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="w-full group-hover:bg-brand group-hover:text-white group-hover:border-brand transition-colors"
                    asChild
                  >
                    <Link to={caseUrl}>
                      {t.viewCase}
                      <ArrowRight className="ml-2 h-3 w-3" />
                    </Link>
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="text-center">
          <Button 
            variant="outline"
            size="lg"
            asChild
          >
            <Link to={t.casesLink}>
              {t.viewAll}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
